import { useEffect } from "react";
import { FileSearch, ScrollText, ShieldCheck, Building2, ArrowRight } from "lucide-react";
import { Reveal, ASSETS } from "@/lib/motion";
import Nav from "@/components/bwg/Nav";
import Footer from "@/components/bwg/Footer";
import WhatsAppFab from "@/components/bwg/WhatsAppFab";
import InteriorHero from "@/components/bwg/InteriorHero";
import FaqSection, { FAQS } from "@/components/bwg/FaqSection";
import Seo from "@/components/bwg/Seo";
import { useBooking } from "@/components/bwg/BookingModal";
import { orgSchema, faqSchema, breadcrumbSchema } from "@/lib/schema";

const BRIEF = [
  "Title verification & encumbrance history",
  "CLU status & Master Plan analysis",
  "Infrastructure developments in radius",
  "Comparable registered transactions",
  "Appreciation potential assessment",
  "Independent legal validation (UP specialists)",
];

export default function AboutUs() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const { open: openBooking } = useBooking();
  return (
    <main className="bg-brand-paper overflow-x-hidden">
      <Seo
        title="About Us | Bhaarat Wealth Group Land Investment Intelligence"
        description="Bhaarat Wealth Group is a land investment intelligence firm sourcing high-conviction land across India's growth corridors, with a focus on Uttar Pradesh. A subsidiary of Philom Tech Pvt. Ltd., New Delhi."
        path="/about-us"
        schema={[orgSchema(), faqSchema(FAQS), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "About Us", path: "/about-us" }])]}
      />
      <Nav overHero />
      <InteriorHero img={ASSETS.metro} eyebrow="About BWG"
        title="Land intelligence, built for conviction."
        sub="We find opportunity where infrastructure, policy and demand converge, before the broader market recognises the value." />

      <section className="bg-brand-offwhite py-24 lg:py-32">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <Reveal>
            <p className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-snug text-brand-ink">
              Bhaarat Wealth Group is a <span className="text-brand-orange">land investment intelligence firm</span> that sources, structures and delivers high-conviction land opportunities across India's strategically positioned growth corridors, with a primary focus on Uttar Pradesh.
            </p>
            <p className="mt-8 text-lg text-brand-grey leading-relaxed">
              Our work is backed by proprietary research, regulatory validation and on-ground intelligence. We identify opportunities where infrastructure, policy and demand converge, and we do the diligence that turns a promising parcel into a defensible investment held in your own name.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Site Intelligence Brief */}
      <section className="bg-brand-paper py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <span className="text-brand-orange text-sm font-semibold tracking-[0.2em] uppercase">Every engagement begins here</span>
            <h2 className="mt-4 font-display font-semibold text-4xl sm:text-5xl tracking-tighter text-brand-ink leading-[1.05]">The Site Intelligence Brief.</h2>
            <p className="mt-6 text-lg text-brand-grey leading-relaxed">
              Before any conversation about capital, we produce a Site Intelligence Brief for the parcel: a single document that tells the whole story the fragmented land record never could.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="grid sm:grid-cols-2 gap-px bg-brand-ink/10 border border-brand-ink/10">
              {BRIEF.map((b, i) => (
                <div key={i} className="bg-brand-card p-6 flex items-start gap-3">
                  <FileSearch className="w-5 h-5 text-brand-orange shrink-0 mt-0.5" />
                  <p className="text-[15px] text-brand-ink leading-snug">{b}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Leadership */}
      <section className="bg-brand-offwhite pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="grid lg:grid-cols-5 gap-10 lg:gap-14 items-center">
              <div className="lg:col-span-3 rounded-sm overflow-hidden border border-brand-ink/10">
                <img src={ASSETS.team} alt="Bhaarat Wealth Group leadership" loading="lazy" className="w-full h-full object-cover" />
              </div>
              <div className="lg:col-span-2">
                <span className="text-brand-orange text-sm font-semibold tracking-[0.2em] uppercase">Leadership</span>
                <h2 className="mt-4 font-display font-semibold text-3xl sm:text-4xl tracking-tighter text-brand-ink leading-tight">Built by operators, not intermediaries.</h2>
                <p className="mt-5 text-brand-grey leading-relaxed">
                  Our founders lead the diligence themselves, from farmer-level conversations to registry verification. The relationships that source a parcel and the judgement that clears it sit with the people whose names are on the firm.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Verification + corporate */}
      <section className="bg-brand-dark py-24 lg:py-32 relative overflow-hidden">
        <div className="grain" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 grid lg:grid-cols-2 gap-14">
          <Reveal>
            <ShieldCheck className="w-9 h-9 text-brand-orange mb-6" strokeWidth={1.5} />
            <h2 className="font-display font-semibold text-3xl sm:text-4xl tracking-tighter text-brand-offwhite leading-tight">Verified before capital is deployed.</h2>
            <p className="mt-5 text-brand-offwhite/65 leading-relaxed">
              Every transaction is independently verified through a network of Uttar Pradesh-specialist legal partners before a single rupee is committed. We treat legal certainty as a precondition, not a formality.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <Building2 className="w-9 h-9 text-brand-orange mb-6" strokeWidth={1.5} />
            <h2 className="font-display font-semibold text-3xl sm:text-4xl tracking-tighter text-brand-offwhite leading-tight">A registered corporate structure.</h2>
            <p className="mt-5 text-brand-offwhite/65 leading-relaxed">
              Bhaarat Wealth Group is a subsidiary of <span className="text-brand-offwhite font-medium">Philom Tech Pvt. Ltd.</span>, a technology and investment holding company based in New Delhi. It is a genuine, verifiable corporate backbone behind every engagement.
            </p>
            <button type="button" onClick={() => openBooking("default", "about_modal")} data-testid="about-book-cta" className="mt-8 inline-flex items-center gap-2 text-brand-orange font-medium hover:text-brand-offwhite transition-colors">
              Book an intelligence briefing <ArrowRight className="w-4 h-4" />
            </button>
          </Reveal>
        </div>
      </section>

      <FaqSection />
      <Footer />
      <WhatsAppFab />
    </main>
  );
}
